import React, { useState, useEffect } from "react";
import { useApp } from "../../context/AppContext";
import { FileText, Plus, Download, Trash2, Edit3, X } from "lucide-react";

const INV_KEY = "fb11_invoices";
function getInvoices() { try { return JSON.parse(localStorage.getItem(INV_KEY) || "[]"); } catch { return []; } }
function saveInvoices(data) { localStorage.setItem(INV_KEY, JSON.stringify(data)); }

export default function Invoice() {
  const { mode } = useApp();
  const [invoices, setInvoices] = useState(getInvoices());
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [msg, setMsg] = useState("");
  const [form, setForm] = useState({
    clientName: "", clientEmail: "", clientAddress: "", clientPhone: "",
    invoiceDate: new Date().toISOString().slice(0, 10),
    dueDate: "",
    items: [{ description: "", qty: 1, rate: 0 }],
    notes: "", gstPercent: 18, discount: 0, status: "Unpaid"
  });

  useEffect(() => { saveInvoices(invoices); }, [invoices]);

  const resetForm = () => {
    setForm({ clientName: "", clientEmail: "", clientAddress: "", clientPhone: "", invoiceDate: new Date().toISOString().slice(0, 10), dueDate: "", items: [{ description: "", qty: 1, rate: 0 }], notes: "", gstPercent: 18, discount: 0, status: "Unpaid" });
    setEditId(null);
    setShowForm(false);
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { description: "", qty: 1, rate: 0 }] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  const updateItem = (idx, field, val) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: field === "description" ? val : parseFloat(val) || 0 };
    setForm({ ...form, items });
  };

  const subtotal = form.items.reduce((s, i) => s + (i.qty * i.rate), 0);
  const discountAmt = (parseFloat(form.discount) || 0);
  const taxable = subtotal - discountAmt;
  const gstAmt = taxable * ((parseFloat(form.gstPercent) || 0) / 100);
  const total = taxable + gstAmt;

  const saveInvoice = () => {
    if (!form.clientName || form.items.length === 0) { setMsg("Client name and at least 1 item required!"); setTimeout(() => setMsg(""), 3000); return; }
    const inv = {
      ...form, id: editId || "INV-" + Date.now().toString(36).toUpperCase(),
      subtotal: Math.round(subtotal), discount: discountAmt, gstAmount: Math.round(gstAmt), total: Math.round(total),
      createdAt: editId ? (invoices.find(i => i.id === editId)?.createdAt || new Date().toISOString()) : new Date().toISOString()
    };
    if (editId) {
      setInvoices(invoices.map(i => i.id === editId ? inv : i));
      setMsg("Invoice updated! ✓");
    } else {
      setInvoices([inv, ...invoices]);
      setMsg("Invoice created! ✓");
    }
    resetForm();
    setTimeout(() => setMsg(""), 3000);
  };

  const deleteInvoice = (id) => { setInvoices(invoices.filter(i => i.id !== id)); setMsg("Invoice deleted ✓"); setTimeout(() => setMsg(""), 2000); };
  const editInvoice = (inv) => { setForm({ ...inv }); setEditId(inv.id); setShowForm(true); };
  const toggleStatus = (id) => {
    setInvoices(invoices.map(i => i.id === id ? { ...i, status: i.status === "Paid" ? "Unpaid" : "Paid" } : i));
  };

  const generatePDF = (inv) => {
    const w = window.open("", "_blank", "width=800,height=1000");
    const items = (inv.items || []);
    w.document.write(`<!DOCTYPE html><html><head><title>Invoice ${inv.id}</title>
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'Segoe UI', sans-serif; padding: 48px; background: #fff; color: #1a1a2e; }
      .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; padding-bottom: 24px; border-bottom: 3px solid #F5A623; }
      .brand h1 { font-size: 32px; color: #F5A623; margin-bottom: 4px; }
      .brand p { font-size: 12px; color: #666; }
      .inv-meta { text-align: right; }
      .inv-meta h2 { font-size: 28px; color: #1a1a2e; letter-spacing: 2px; }
      .inv-meta p { font-size: 12px; color: #666; margin-top: 4px; }
      .parties { display: flex; justify-content: space-between; margin-bottom: 32px; }
      .party { flex: 1; }
      .party-label { font-size: 10px; text-transform: uppercase; letter-spacing: 1.5px; color: #999; font-weight: 700; margin-bottom: 8px; }
      .party-name { font-size: 16px; font-weight: 700; color: #1a1a2e; }
      .party-detail { font-size: 12px; color: #666; line-height: 1.6; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
      th { background: #1a1a2e; color: #fff; padding: 12px 16px; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; text-align: left; }
      th:last-child, th:nth-child(3), th:nth-child(4) { text-align: right; }
      td { padding: 12px 16px; border-bottom: 1px solid #eee; font-size: 13px; color: #333; }
      td:last-child, td:nth-child(3), td:nth-child(4) { text-align: right; }
      .totals { display: flex; justify-content: flex-end; margin-bottom: 32px; }
      .totals-box { width: 300px; }
      .total-row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 13px; color: #666; border-bottom: 1px solid #f0f0f0; }
      .total-row.grand { padding: 14px 0; font-size: 18px; font-weight: 800; color: #1a1a2e; border-top: 2px solid #1a1a2e; border-bottom: none; margin-top: 8px; }
      .status { display: inline-block; padding: 6px 16px; border-radius: 20px; font-size: 12px; font-weight: 700; }
      .status-paid { background: #dcfce7; color: #16a34a; }
      .status-unpaid { background: #fef2f2; color: #dc2626; }
      .notes { margin-top: 32px; padding: 16px; background: #f8f9fa; border-radius: 8px; font-size: 12px; color: #666; }
      .notes strong { color: #333; }
      .footer { text-align: center; margin-top: 48px; padding-top: 20px; border-top: 1px solid #eee; font-size: 11px; color: #bbb; }
      @media print { body { padding: 24px; } }
    </style></head><body>
    <div class="header">
      <div class="brand"><h1>FinBoard</h1><p>Stake Affiliate Financial Command Center</p></div>
      <div class="inv-meta">
        <h2>INVOICE</h2>
        <p><strong>${inv.id}</strong></p>
        <p>Date: ${inv.invoiceDate}${inv.dueDate ? " | Due: " + inv.dueDate : ""}</p>
        <span class="status ${inv.status === "Paid" ? "status-paid" : "status-unpaid"}">${inv.status}</span>
      </div>
    </div>
    <div class="parties">
      <div class="party">
        <div class="party-label">From</div>
        <div class="party-name">FinBoard</div>
        <div class="party-detail">Stake Affiliate<br>Financial Management</div>
      </div>
      <div class="party" style="text-align:right">
        <div class="party-label">Bill To</div>
        <div class="party-name">${inv.clientName}</div>
        <div class="party-detail">${inv.clientEmail ? inv.clientEmail + "<br>" : ""}${inv.clientPhone ? inv.clientPhone + "<br>" : ""}${inv.clientAddress || ""}</div>
      </div>
    </div>
    <table>
      <thead><tr><th>#</th><th>Description</th><th>Qty</th><th>Rate</th><th>Amount</th></tr></thead>
      <tbody>${items.map((item, i) => "<tr><td>" + (i + 1) + "</td><td>" + (item.description || "Item") + "</td><td style='text-align:right'>" + item.qty + "</td><td style='text-align:right'>₹" + (item.rate || 0).toLocaleString() + "</td><td style='text-align:right'>₹" + (item.qty * item.rate).toLocaleString() + "</td></tr>").join("")}</tbody>
    </table>
    <div class="totals"><div class="totals-box">
      <div class="total-row"><span>Subtotal</span><span>₹${(inv.subtotal || 0).toLocaleString()}</span></div>
      ${inv.discount > 0 ? '<div class="total-row"><span>Discount</span><span>-₹' + inv.discount.toLocaleString() + "</span></div>" : ""}
      <div class="total-row"><span>GST (${inv.gstPercent || 0}%)</span><span>₹${(inv.gstAmount || 0).toLocaleString()}</span></div>
      <div class="total-row grand"><span>Total</span><span>₹${(inv.total || 0).toLocaleString()}</span></div>
    </div></div>
    ${inv.notes ? '<div class="notes"><strong>Notes:</strong> ' + inv.notes + "</div>" : ""}
    <div class="footer"><p>Generated by FinBoard v11.3 | ${new Date().toLocaleDateString()}</p></div>
    <script>setTimeout(function(){window.print()},500)</script>
    </body></html>`);
  };

  const cardStyle = { padding: 24, borderRadius: 16, background: "rgba(15,23,42,.6)", border: "1px solid rgba(100,116,139,.15)", marginBottom: 20 };
  const inputStyle = { width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(100,116,139,.25)", background: "rgba(15,23,42,.8)", color: "#E2E8F0", fontSize: 13, outline: "none", boxSizing: "border-box" };
  const badgeStyle = (paid) => ({ padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: paid ? "rgba(16,185,129,.1)" : "rgba(239,68,68,.1)", color: paid ? "#10B981" : "#EF4444", border: "1px solid " + (paid ? "rgba(16,185,129,.2)" : "rgba(239,68,68,.2)"), cursor: "pointer" });

  const totalRevenue = invoices.filter(i => i.status === "Paid").reduce((s, i) => s + (i.total || 0), 0);
  const totalPending = invoices.filter(i => i.status === "Unpaid").reduce((s, i) => s + (i.total || 0), 0);

  return (
    <div style={{ padding: "24px 32px", maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: "#E2E8F0", margin: 0 }}>
            <FileText size={22} style={{ verticalAlign: "middle", marginRight: 10, color: "#F5A623" }} />
            Invoice Generator
          </h2>
          <p style={{ color: "#64748B", fontSize: 13, margin: "4px 0 0" }}>Admin &gt; Invoices</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }} style={{ padding: "10px 20px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#F5A623,#E8960F)", color: "#000", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
          <Plus size={16} /> New Invoice
        </button>
      </div>

      {msg && <div style={{ padding: "10px 16px", borderRadius: 10, marginBottom: 16, fontSize: 13, fontWeight: 600, background: msg.includes("✓") ? "rgba(16,185,129,.1)" : "rgba(239,68,68,.1)", color: msg.includes("✓") ? "#10B981" : "#EF4444", border: "1px solid " + (msg.includes("✓") ? "rgba(16,185,129,.2)" : "rgba(239,68,68,.2)") }}>{msg}</div>}

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
        {[
          { label: "Total Invoices", value: invoices.length, color: "#3B82F6" },
          { label: "Paid", value: invoices.filter(i => i.status === "Paid").length, color: "#10B981" },
          { label: "Revenue", value: "₹" + totalRevenue.toLocaleString(), color: "#10B981" },
          { label: "Pending", value: "₹" + totalPending.toLocaleString(), color: "#EF4444" }
        ].map(s => (
          <div key={s.label} style={{ ...cardStyle, textAlign: "center", padding: 20, marginBottom: 0 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Invoice Form */}
      {showForm && (
        <div style={cardStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: "#E2E8F0", margin: 0 }}>{editId ? "Edit Invoice" : "New Invoice"}</h3>
            <button onClick={resetForm} style={{ background: "none", border: "none", color: "#64748B", cursor: "pointer" }}><X size={18} /></button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#64748B", display: "block", marginBottom: 4 }}>Client Name *</label>
              <input value={form.clientName} onChange={e => setForm({ ...form, clientName: e.target.value })} placeholder="Company/Person name" style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#64748B", display: "block", marginBottom: 4 }}>Client Email</label>
              <input value={form.clientEmail} onChange={e => setForm({ ...form, clientEmail: e.target.value })} placeholder="email@company.com" style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#64748B", display: "block", marginBottom: 4 }}>Client Phone</label>
              <input value={form.clientPhone} onChange={e => setForm({ ...form, clientPhone: e.target.value })} placeholder="+91 98765 43210" style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#64748B", display: "block", marginBottom: 4 }}>Client Address</label>
              <input value={form.clientAddress} onChange={e => setForm({ ...form, clientAddress: e.target.value })} placeholder="City, State" style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#64748B", display: "block", marginBottom: 4 }}>Invoice Date</label>
              <input type="date" value={form.invoiceDate} onChange={e => setForm({ ...form, invoiceDate: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#64748B", display: "block", marginBottom: 4 }}>Due Date</label>
              <input type="date" value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })} style={inputStyle} />
            </div>
          </div>

          <h4 style={{ fontSize: 13, fontWeight: 700, color: "#94A3B8", marginBottom: 12, textTransform: "uppercase", letterSpacing: 1 }}>Items</h4>
          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 12 }}>
            <thead>
              <tr>
                {["Description", "Qty", "Rate (₹)", "Amount", ""].map(h => (
                  <th key={h} style={{ padding: "8px 10px", textAlign: h === "Description" ? "left" : "right", fontSize: 11, fontWeight: 700, color: "#64748B", borderBottom: "1px solid rgba(100,116,139,.15)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {form.items.map((item, idx) => (
                <tr key={idx}>
                  <td style={{ padding: "6px 4px" }}><input value={item.description} onChange={e => updateItem(idx, "description", e.target.value)} placeholder="Item description" style={{ ...inputStyle, padding: "8px 10px" }} /></td>
                  <td style={{ padding: "6px 4px", width: 80 }}><input type="number" min="1" value={item.qty} onChange={e => updateItem(idx, "qty", e.target.value)} style={{ ...inputStyle, padding: "8px 10px", textAlign: "right" }} /></td>
                  <td style={{ padding: "6px 4px", width: 120 }}><input type="number" min="0" value={item.rate} onChange={e => updateItem(idx, "rate", e.target.value)} style={{ ...inputStyle, padding: "8px 10px", textAlign: "right" }} /></td>
                  <td style={{ padding: "6px 10px", textAlign: "right", fontSize: 13, fontWeight: 600, color: "#E2E8F0", width: 120 }}>₹{(item.qty * item.rate).toLocaleString()}</td>
                  <td style={{ padding: "6px 4px", width: 40 }}>
                    {form.items.length > 1 && <button onClick={() => removeItem(idx)} style={{ background: "none", border: "none", color: "#EF4444", cursor: "pointer", padding: 4 }}><Trash2 size={14} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={addItem} style={{ padding: "6px 14px", borderRadius: 6, border: "1px dashed rgba(100,116,139,.3)", background: "transparent", color: "#64748B", fontSize: 12, cursor: "pointer", marginBottom: 20 }}>+ Add Item</button>

          <div style={{ display: "flex", gap: 20, marginBottom: 20 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#64748B", display: "block", marginBottom: 4 }}>Discount (₹)</label>
              <input type="number" min="0" value={form.discount} onChange={e => setForm({ ...form, discount: e.target.value })} style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#64748B", display: "block", marginBottom: 4 }}>GST %</label>
              <input type="number" min="0" max="28" value={form.gstPercent} onChange={e => setForm({ ...form, gstPercent: e.target.value })} style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#64748B", display: "block", marginBottom: 4 }}>Notes</label>
              <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Payment terms, etc." style={inputStyle} />
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 20 }}>
            <div style={{ width: 280, padding: 16, borderRadius: 10, background: "rgba(245,166,35,.04)", border: "1px solid rgba(245,166,35,.1)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 13, color: "#94A3B8" }}><span>Subtotal</span><span>₹{Math.round(subtotal).toLocaleString()}</span></div>
              {discountAmt > 0 && <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 13, color: "#EF4444" }}><span>Discount</span><span>-₹{Math.round(discountAmt).toLocaleString()}</span></div>}
              <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 13, color: "#94A3B8" }}><span>GST ({form.gstPercent}%)</span><span>₹{Math.round(gstAmt).toLocaleString()}</span></div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0 0", fontSize: 18, fontWeight: 800, color: "#F5A623", borderTop: "2px solid rgba(245,166,35,.2)", marginTop: 8 }}><span>Total</span><span>₹{Math.round(total).toLocaleString()}</span></div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={saveInvoice} style={{ padding: "10px 28px", borderRadius: 8, border: "none", background: "linear-gradient(135deg,#F5A623,#E8960F)", color: "#000", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{editId ? "Update Invoice" : "Create Invoice"}</button>
            <button onClick={resetForm} style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid rgba(100,116,139,.3)", background: "transparent", color: "#94A3B8", fontSize: 13, cursor: "pointer" }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Invoice List */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: "#E2E8F0", marginBottom: 16 }}>All Invoices</h3>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {["Invoice #", "Client", "Date", "Amount", "GST", "Total", "Status", "Actions"].map(h => (
                <th key={h} style={{ padding: "10px 12px", textAlign: h === "Actions" ? "center" : "left", fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase", borderBottom: "1px solid rgba(100,116,139,.15)" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {invoices.map(inv => (
              <tr key={inv.id} style={{ borderBottom: "1px solid rgba(100,116,139,.08)" }}>
                <td style={{ padding: "12px", fontSize: 13, fontWeight: 600, color: "#F5A623" }}>{inv.id}</td>
                <td style={{ padding: "12px", fontSize: 13, color: "#E2E8F0" }}>{inv.clientName}</td>
                <td style={{ padding: "12px", fontSize: 12, color: "#94A3B8" }}>{inv.invoiceDate}</td>
                <td style={{ padding: "12px", fontSize: 13, color: "#E2E8F0" }}>₹{(inv.subtotal || 0).toLocaleString()}</td>
                <td style={{ padding: "12px", fontSize: 12, color: "#94A3B8" }}>₹{(inv.gstAmount || 0).toLocaleString()}</td>
                <td style={{ padding: "12px", fontSize: 13, fontWeight: 700, color: "#E2E8F0" }}>₹{(inv.total || 0).toLocaleString()}</td>
                <td style={{ padding: "12px" }}>
                  <span onClick={() => toggleStatus(inv.id)} style={badgeStyle(inv.status === "Paid")}>{inv.status}</span>
                </td>
                <td style={{ padding: "12px", textAlign: "center" }}>
                  <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                    <button onClick={() => generatePDF(inv)} title="Download PDF" style={{ padding: "4px 8px", borderRadius: 6, border: "none", background: "rgba(59,130,246,.12)", color: "#3B82F6", cursor: "pointer" }}><Download size={14} /></button>
                    <button onClick={() => editInvoice(inv)} title="Edit" style={{ padding: "4px 8px", borderRadius: 6, border: "none", background: "rgba(245,166,35,.12)", color: "#F5A623", cursor: "pointer" }}><Edit3 size={14} /></button>
                    <button onClick={() => deleteInvoice(inv.id)} title="Delete" style={{ padding: "4px 8px", borderRadius: 6, border: "none", background: "rgba(239,68,68,.12)", color: "#EF4444", cursor: "pointer" }}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {invoices.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 48, textAlign: "center", color: "#475569", fontSize: 13 }}>No invoices yet. Click "New Invoice" to create one!</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
